import { clsx } from 'clsx';
import { forwardRef } from 'react';
import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from 'react';

type ButtonVariant = 'primary' | 'secondary' | 'ghost';
type ButtonSize = 'md' | 'lg';

type CommonProps = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  children: ReactNode;
};

type ButtonAsButton = CommonProps & {
  as?: 'button';
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof CommonProps>;

type ButtonAsAnchor = CommonProps & {
  as: 'a';
} & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof CommonProps>;

type ButtonProps = ButtonAsButton | ButtonAsAnchor;

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary:
    'bg-gold-gradient bg-shimmer text-night shadow-goldMedium hover:shadow-goldStrong border border-gold/40',
  secondary:
    'bg-transparent text-gold border border-gold/60 hover:border-gold hover:bg-gold/5 hover:shadow-goldSoft',
  ghost: 'bg-transparent text-champagne border border-transparent hover:text-gold',
};

const SIZE_CLASSES: Record<ButtonSize, string> = {
  md: 'px-6 py-3 text-base',
  lg: 'px-10 py-4 text-lg',
};

const BASE_CLASSES =
  'inline-flex items-center justify-center gap-2 rounded-md font-display font-semibold tracking-wide transition-all duration-base ease-ceremonial focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-gold disabled:opacity-50 disabled:cursor-not-allowed';

export const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  function Button(props, ref) {
    const {
      variant = 'primary',
      size = 'md',
      fullWidth = false,
      className,
      children,
      ...rest
    } = props as CommonProps & {
      as?: 'button' | 'a';
      className?: string;
      [key: string]: unknown;
    };

    const composedClassName = clsx(
      BASE_CLASSES,
      VARIANT_CLASSES[variant],
      SIZE_CLASSES[size],
      fullWidth && 'w-full',
      className,
    );

    if (props.as === 'a') {
      const { as: _as, ...anchorProps } = rest as Record<string, unknown>;
      void _as;
      return (
        <a
          ref={ref as React.Ref<HTMLAnchorElement>}
          className={composedClassName}
          {...(anchorProps as AnchorHTMLAttributes<HTMLAnchorElement>)}
        >
          {children}
        </a>
      );
    }

    const { as: _as, ...buttonProps } = rest as Record<string, unknown>;
    void _as;
    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        type={(buttonProps as { type?: 'button' | 'submit' | 'reset' }).type ?? 'button'}
        className={composedClassName}
        {...(buttonProps as ButtonHTMLAttributes<HTMLButtonElement>)}
      >
        {children}
      </button>
    );
  },
);
